import { Button, Card, CardContent, Container, LinearProgress, Typography } from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import jsonp from "jsonp";
import { Helmet } from "react-helmet";
import { Suspense, useEffect } from "react";
import {
  Await,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  useLoaderData,
  useRouteError,
  useSearchParams,
} from "react-router-dom";
import config from "./config";
import { TokenContext } from "./context/TokenContext";
import { MissingIntegrationPageSearchParams, TokenNotValid } from "./error/error";
import { InSiteContext } from "./context/InSiteContext";
// import Index from "./pages/Index";
import { LoaderData } from "./types/types";
import BusinessVisitors from "./pages/BusinessVisitors";

async function getIntegrationSettings(dataurl: string) {
  try {
    const integrationSettings = await new Promise((resolve, reject) => {
      jsonp(dataurl, {}, function (err: Error | null, data: { scriptUrl: string; cssUrl: string }) {
        if (err) {
          console.error(err.message);
          reject(err);
        } else {
          resolve({
            scriptUrl: data.scriptUrl,
            cssUrl: data.cssUrl,
          });
        }
      });
    }).then((res) => res);
    return integrationSettings;
  } catch (e) {
    console.warn(e);
  }
}

function ErrorBoundary() {
  const error = useRouteError();
  if (error instanceof TokenNotValid) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <Card variant="outlined">
            <CardContent sx={{ minWidth: "300px", maxWidth: "550px" }}>
              <Typography variant="h5" component="h2" gutterBottom>
                AFAS Error
              </Typography>
              <Typography variant="body1">
                Er is een fout opgetreden bij het communiceren met AFAS Profit. Controleer of u verbonden bent
                met het internet en of uw inloggegevens correct zijn. Probeer het later opnieuw of neem
                contact op met de systeembeheerder als het probleem aanhoudt.
              </Typography>
              <Button
                sx={{ mt: 2 }}
                onClick={() => {
                  window.location.reload();
                }}
                variant="outlined"
              >
                Refresh pagina
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  if (error instanceof MissingIntegrationPageSearchParams) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <Card variant="outlined">
            <CardContent sx={{ minWidth: "300px", maxWidth: "550px" }}>
              <Typography variant="h5" component="h2" gutterBottom>
                Ontbrekende query parameters
              </Typography>
              <Typography variant="body1">
                Er ontbreken enkele query parameters om deze pagina te laden. Een mogelijke reden is dat de
                pagina niet via een integratie pagina wordt geladen. Controleer of u de juiste URL heeft
                gebruikt en of u alle benodigde parameters heeft ingevuld. Probeer het later opnieuw of neem
                contact op met de systeembeheerder als het probleem aanhoudt.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Card variant="outlined">
          <CardContent>Error</CardContent>
        </Card>
      </Box>
    </Container>
  );
}

function LoadingPage() {
  return (
    <>
      <Box mb={2}>
        <LinearProgress />
      </Box>
    </>
  );
}

const GladiorWrapper: React.FC<{
  gladiorToken: string;
  integrationSettings: { scriptUrl: string; cssUrl: string };
  insiteUrl: string;
  children: React.ReactNode;
}> = ({ gladiorToken, integrationSettings, insiteUrl, children }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    searchParams.set("gladiorToken", gladiorToken);
    setSearchParams(searchParams);
  }, [gladiorToken, searchParams, setSearchParams]);

  return (
    <TokenContext.Provider value={gladiorToken}>
      <InSiteContext.Provider value={insiteUrl}>
        <Helmet>
          <link rel="stylesheet" href={integrationSettings.cssUrl} type="text/css" />
          <script src={integrationSettings.scriptUrl} type="text/javascript" />
        </Helmet>
        {children}
      </InSiteContext.Provider>
    </TokenContext.Provider>
  );
};

function Wrapper() {
  const { gladiorToken, insiteUrl, integrationSettings } = useLoaderData() as LoaderData;
  return (
    <Suspense fallback={<LoadingPage />}>
      <Await resolve={gladiorToken}>
        {(resolvedToken) => (
          <Await resolve={insiteUrl}>
            {(resolvedInsiteUrl) => (
              <Await resolve={integrationSettings}>
                {(resolvedIntegrationSettings) => {
                  return (
                    <GladiorWrapper
                      gladiorToken={resolvedToken}
                      integrationSettings={resolvedIntegrationSettings}
                      insiteUrl={resolvedInsiteUrl}
                    >
                      <Outlet />
                    </GladiorWrapper>
                  );
                }}
              </Await>
            )}
          </Await>
        )}
      </Await>
    </Suspense>
  );
}

async function getGladiorToken(tokenurl: string, code: string, sessionid: string, publickey: string) {
  // if (!(window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) {
  try {
    const {
      data: { token },
    } = await axios.post(`${config.BASE_URL}/token`, {
      tokenurl,
      code,
      sessionid,
      publickey,
    });
    return token;
  } catch (error) {
    throw new TokenNotValid("Token not valid");
  }
  // } else {
  //   await new Promise((r) => setTimeout(r, 2000));
  //   return "TEST TOKEN_GLADIOR";
  // }
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={<Wrapper />}
      errorElement={<ErrorBoundary />}
      loader={async ({ request }) => {
        const searchParams = new URL(request.url).searchParams;
        const tokenurl = searchParams.get("tokenurl") ?? "";
        const code = searchParams.get("code") ?? "";
        const sessionid = searchParams.get("sessionid") ?? "";
        const publickey = searchParams.get("publickey") ?? "";
        const dataurl = searchParams.get("dataurl") ?? "";
        const gladiorToken = searchParams.get("gladiorToken");

        if (!tokenurl || !code || !sessionid || !publickey) {
          throw new MissingIntegrationPageSearchParams("Missing params");
        }
        const integrationSettings = getIntegrationSettings(dataurl);
        if (!gladiorToken) {
          const gladiorTokenResponse = getGladiorToken(tokenurl, code, sessionid, publickey);
          return {
            gladiorToken: gladiorTokenResponse,
            insiteUrl: tokenurl.replace("/integrationtoken", ""),
            integrationSettings,
          };
        } else {
          return {
            gladiorToken: gladiorToken,
            insiteUrl: tokenurl.replace("/integrationtoken", ""),
            integrationSettings,
          };
        }
      }}
    >
      <Route path="/" element={<BusinessVisitors />} />
      <Route path="bedrijven/" element={<BusinessVisitors />} />
      <Route path="brochure-aanvraag/" element={<BusinessVisitors />} />
    </Route>
  )
);

function Root() {
  return <RouterProvider router={router} />;
}

export default Root;
